import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useEffect } from 'react'
import Breadcrumbs from '../components/Breadcrumbs'

export default function SolutionsPage() {
  const { t } = useTranslation()
  const [ref] = useInView({ triggerOnce: true, threshold: 0.05 })

  useEffect(() => {
    document.title = `${t('solutions.pageTitle')} | NiceDay Technologies`
  }, [t])

  return (
    <div className="pb-16">
      <Breadcrumbs items={[{ key: 'solutions' }]} />
      {/* Page hero */}
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-primary font-semibold uppercase tracking-wider text-sm mb-4">
              {t('solutions.title')}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              {t('solutions.pageHeadline')}
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              {t('solutions.pageSubheadline')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Security */}
      <section className="py-16 md:py-24" ref={ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-12 lg:gap-16">
            <div className="lg:w-1/2 space-y-6">
              <h2 className="text-accent font-bold uppercase tracking-wider">
                {t('solutions.security.number')}. {t('solutions.security.category')}
              </h2>
              <h3 className="text-3xl sm:text-4xl font-bold">{t('solutions.security.title')}</h3>
              <p className="text-lg text-slate-600 dark:text-slate-400">
                {t('solutions.security.description')}
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {[
                  { icon: 'security', key: 'turnstiles' },
                  { icon: 'videocam', key: 'cameras' },
                  { icon: 'monitor', key: 'monitors' },
                  { icon: 'analytics', key: 'analytics' },
                ].map((feature) => (
                  <li
                    key={feature.key}
                    className="flex items-start gap-3 p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm"
                  >
                    <span className="material-icons text-primary shrink-0">{feature.icon}</span>
                    <span>{t(`solutions.security.features.${feature.key}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:w-1/2 w-full">
              <div className="bg-slate-200 dark:bg-slate-800 rounded-2xl sm:rounded-3xl p-6 sm:p-8 overflow-hidden group">
                <img
                  alt="Security surveillance systems"
                  className="rounded-xl sm:rounded-2xl shadow-lg w-full object-cover transition-transform group-hover:scale-105 duration-700"
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuCgL1e5ltN_VCkuZCXY0d-m0xaq9xEPDD1i4e8NT_WKWHEl1tA_zXZxIoTusqi-_ynKDq--aeGMjDnoikMUzrHXpAD_CYEAurlrkRtiaR7JG0sLkS__zEYzrxYAVFCRvZeaay_4xduONnxHZzsoWc_jNMNF0B2t-HKCF5Z2magsK3g2PjTw8XPN4ezkMvHloRnvFM5-PHXJNHMrC-yhEwDeB47mOmKvjLaAcmJAjpw9lMXl9tAKQ-vMghLTwgRhVcsr0jZavJTTJg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience */}
      <section className="py-16 md:py-24 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="lg:w-1/2 space-y-6 order-2 lg:order-1">
              <h2 className="text-primary font-bold uppercase tracking-wider">
                {t('solutions.experience.number')}. {t('solutions.experience.category')}
              </h2>
              <h3 className="text-3xl sm:text-4xl font-bold">{t('solutions.experience.title')}</h3>
              <p className="text-lg text-slate-600 dark:text-slate-400">
                {t('solutions.experience.description')}
              </p>
              <div className="space-y-4">
                {[
                  { icon: 'queue', title: 'queue', desc: 'queueDesc' },
                  { icon: 'tv', title: 'display', desc: 'displayDesc' },
                  { icon: 'reviews', title: 'satisfaction', desc: 'satisfactionDesc' },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="flex gap-4 p-4 rounded-2xl hover:bg-white dark:hover:bg-slate-800 transition-all group"
                  >
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-white transition-colors">
                      <span className="material-icons">{item.icon}</span>
                    </div>
                    <div>
                      <h4 className="font-bold">{t(`solutions.experience.${item.title}`)}</h4>
                      <p className="text-slate-500 text-sm sm:text-base">
                        {t(`solutions.experience.${item.desc}`)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:w-1/2 w-full order-1 lg:order-2">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <img
                  alt="Customer kiosk 1"
                  className="rounded-xl sm:rounded-2xl shadow-lg w-full object-cover mt-8 sm:mt-12"
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuASfauwfS16li7fCKGwxXQpEuGC9uVqtFWr7w5cntDjD9srcTg2HhFpg84z2YSpgC_y6krAy0U0ScAKWt-xFvqvbFTJuyJ3OXsMgvwZ_s1qLOVIV8810r6Jkz3BdChtiq6jMTKp8YDYsLxK6G7MNiSlOWsll07nIHrg2Kq3k8JSeARBc144YFN1RQvtjAMyn40vVBJbBwPxSUfqUErV_9lKfQjFdqvC3Ff3k4M0YVfWQioK64oXhsqaqnVVJSRqB_2HdxUmJyU7MA"
                />
                <img
                  alt="Customer kiosk 2"
                  className="rounded-xl sm:rounded-2xl shadow-lg w-full object-cover"
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuDdrhUX_Kr6BzWc56ODRtIvjcLw-dvwMR6at1F9mOJoKPtEnspqOou_t-CkMKlkv-Vrgq6X4LEhb-xKCJv0ivLHPNBljx_1zdD7365ta9JQ9wRtx-S6WGyW5qg1r0U5pNUnaHebAQ7jZ5vvUGYEDVKb-da6CtZ2RVudh1PHKkKSbja7B_cV9r0S-r1FzIJFLYvYu0lXEZolYsvYiOfXKjqEmE7SgQEmll1c4ePwRdPEIWj68YO3HrG6-Px8Rvp2s4SjNSgWQRgG_g"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-12 lg:gap-16">
            <div className="lg:w-1/2 space-y-6">
              <h2 className="text-accent font-bold uppercase tracking-wider">
                {t('solutions.payment.number')}. {t('solutions.payment.category')}
              </h2>
              <h3 className="text-3xl sm:text-4xl font-bold">{t('solutions.payment.title')}</h3>
              <p className="text-lg text-slate-600 dark:text-slate-400">
                {t('solutions.payment.description')}
              </p>
              <div className="p-6 bg-primary text-white rounded-2xl shadow-xl shadow-primary/20">
                <div className="flex items-center gap-4 mb-4">
                  <span className="material-icons text-3xl">point_of_sale</span>
                  <h4 className="text-xl font-bold">{t('solutions.payment.posTitle')}</h4>
                </div>
                <ul className="space-y-2 opacity-90">
                  {['fast', 'detection', 'nfc'].map((key) => (
                    <li key={key} className="flex items-center gap-2">
                      <span className="material-icons text-sm">check_circle</span>
                      {t(`solutions.payment.${key}`)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="lg:w-1/2 w-full">
              <img
                alt="Payment terminal"
                className="rounded-2xl sm:rounded-3xl shadow-2xl w-full object-cover"
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuDRoZv1EcwqZ8b3k1_Wttgu8SlYAcRFnpfuzxbU1M4m1WvMOWL8LGk5Q7f3kB_aCOTIiIGJHWAUTF8krvkodPELYR2A89BrZYxP01Md4N2v3JJP6J4DKFJ1AF2id0PUVsEm4MR0nxaBQLwkJmR1-sUVZypL8ymxYyjhN2t13VWsXLQ_jaAXyPJ-E0y6cK3FHjoSD9RbObI0xVeeO3CsmTR63SlyYz1Y9AQIGwgTfHEPKUpds3UROZbaIMCghGnbM770ANSBpHV17Q"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Software */}
      <section className="py-16 md:py-24 bg-secondary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-primary font-bold uppercase tracking-wider mb-4">
            {t('solutions.software.number')}. {t('solutions.software.category')}
          </h2>
          <h3 className="text-3xl sm:text-4xl font-bold mb-12 md:mb-16">
            {t('solutions.software.title')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 text-left">
            {[
              { key: 'appointments', icon: 'event_available' },
              { key: 'erp', icon: 'integration_instructions' },
              { key: 'communication', icon: 'chat' },
              { key: 'cloud', icon: 'cloud_done' },
            ].map((item) => (
              <div
                key={item.key}
                className="bg-white/10 p-6 sm:p-8 rounded-2xl backdrop-blur-sm border border-white/10 hover:bg-white/20 transition-all"
              >
                <span className="material-icons text-primary text-4xl mb-4 sm:mb-6 block">
                  {item.icon}
                </span>
                <h4 className="text-lg sm:text-xl font-bold mb-3">
                  {t(`solutions.software.${item.key}.title`)}
                </h4>
                <p className="text-white/70 text-sm">{t(`solutions.software.${item.key}.description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">{t('contact.title')}</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-8 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-4 bg-primary text-white rounded-xl font-bold text-lg hover:scale-105 transition-transform shadow-xl shadow-primary/30"
          >
            {t('contact.ctaExpert')}
          </Link>
        </div>
      </section>
    </div>
  )
}
